import { Chip, Paper, Typography } from '@mui/material'
import { styled } from '@mui/material/styles'

import { Box } from '@/components/ui'
import { rolloutOutcomeDisplay, type RolloutResult } from '@/definitions/featureFlags'
import { spacing } from '@/themes/tokens'

import { summarizeRollout } from '../../utils/summarizeRollout'

const ScrollingResults = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(spacing.sm),
  maxHeight: 320,
  overflowY: 'auto',
  paddingRight: theme.spacing(spacing.sm),
}))

/** Higher sorts first: anything the operator still has to act on. */
const severityRank = (result: RolloutResult): number => {
  const { severity } = rolloutOutcomeDisplay(result.outcome)
  return severity === 'error' || severity === 'warning' ? 1 : 0
}

interface RolloutResultsProps {
  results: RolloutResult[]
}

/**
 * What the last run did, organization by organization.
 *
 * Ordered with the organizations that still lack the flag first: a run of 31 is
 * read to find the handful that need chasing, and putting them at the top means
 * the operator does not scroll past thirty successes to find them.
 */
export const RolloutResults = ({ results }: RolloutResultsProps) => {
  const summary = summarizeRollout(results)
  const ordered = [...results].sort((left, right) => severityRank(right) - severityRank(left))

  return (
    <Box variant='flexColumn' gap='md'>
      <Typography variant='subtitle2'>
        {summary.moved} changed, {summary.already} already there,{' '}
        {summary.needsAttention} needing attention
      </Typography>

      <ScrollingResults>
        {ordered.map((result) => {
          const { label, severity } = rolloutOutcomeDisplay(result.outcome)
          return (
            <Paper key={result.org_id} variant='infoRow'>
              <Box variant='flexBetween'>
                <Box variant='flexColumn' gap='none' fullWidth={false} minWidth={0}>
                  <Typography variant='body2'>
                    {result.display_name ?? result.org_id}
                  </Typography>
                  {result.detail !== null && (
                    <Typography variant='caption' color='text.secondary'>
                      {result.detail}
                    </Typography>
                  )}
                </Box>
                <Chip
                  size='small'
                  label={label}
                  color={severity === 'info' ? 'default' : severity}
                />
              </Box>
            </Paper>
          )
        })}
      </ScrollingResults>
    </Box>
  )
}
